// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { SerializedLabel, SerializedAttribute } from 'cvat-core-wrapper';

export interface SkeletonConfiguration {
    type: 'skeleton';
    svg: string;
    sublabels: SerializedLabel[];
}

export type LabelOptColor = SerializedLabel;

let id = 0;

function validateParsedAttribute(attr: SerializedAttribute): void {
    if (typeof attr.name !== 'string') {
        throw new Error(`属性名必须是 string。得到：${attr.name}`);
    }

    if (!['number', 'undefined'].includes(typeof attr.id)) {
        throw new Error(
            `属性："${attr.name}". 属性标识必须是 number 或 undefined。得到：${attr.id}`,
        );
    }

    if (!['checkbox', 'number', 'text', 'radio', 'select'].includes((attr.input_type || '').toLowerCase())) {
        throw new Error(`属性："${attr.name}"。未知的输入类型：${attr.input_type}`);
    }

    if (typeof attr.mutable !== 'boolean') {
        throw new Error(`属性："${attr.name}"。'是否可变'的值必须是布尔类型。得到类型：${attr.mutable}`);
    }

    if (!Array.isArray(attr.values)) {
        throw new Error(`属性："${attr.name}"。属性值必须是一个数组。得到类型：${typeof attr.values}`);
    }

    if (!attr.values.length) {
        throw new Error(`属性："${attr.name}"。属性值数组不能为空`);
    }

    for (const value of attr.values) {
        if (typeof value !== 'string') {
            throw new Error(`属性："${attr.name}"。属性值必须都是字符串。得到类型：${value}`);
        }
    }
}

export function validateParsedLabel(label: SerializedLabel): void {
    if (typeof label.name !== 'string') {
        throw new Error(`标签名必须是 string。得到：${label.name}`);
    }

    if (!['number', 'undefined'].includes(typeof label.id)) {
        throw new Error(
            `标签 "${label.name}"。标签标识必须是 number 或 undefined。得到：${label.id}`,
        );
    }

    if (label.color && typeof label.color !== 'string') {
        throw new Error(`标签："${label.name}"。标签颜色必须是 string。得到：${typeof label.color}`);
    }

    if (label.color && !label.color.match(/^#[0-9a-fA-F]{6}$|^$/)) {
        throw new Error(
            `标签 "${label.name}"。` +
                `标签颜色必须是一个有效的颜色序列。得到：${label.color}`,
        );
    }

    if (!Array.isArray(label.attributes)) {
        throw new Error(`标签 "${label.name}"。属性必须是一个 array。得到：${typeof label.attributes}`);
    }

    for (const attr of label.attributes) {
        validateParsedAttribute(attr);
    }
}

export function idGenerator(): number {
    return --id;
}

export function equalArrayHead(arr1: string[], arr2: string[]): boolean {
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }

    return true;
}

export function toSVGCoord(svg: SVGSVGElement, coord: number[], raiseError = false): number[] {
    const result = [];
    const ctm = svg.getScreenCTM();

    if (!ctm) {
        if (raiseError) throw new Error('Screen CTM is null');
        return coord;
    }

    const inversed = ctm.inverse();
    if (!inversed) {
        if (raiseError) throw new Error('Inversed screen CTM is null');
        return coord;
    }

    for (let i = 0; i < coord.length; i += 2) {
        let point = svg.createSVGPoint();
        point.x = coord[i];
        point.y = coord[i + 1];
        point = point.matrixTransform(inversed);
        result.push(point.x, point.y);
    }

    return result;
}

export function fromSVGCoord(svg: SVGSVGElement, coord: number[], raiseError = false): number[] {
    const result = [];
    const ctm = svg.getCTM();
    if (!ctm) {
        if (raiseError) throw new Error('Inversed screen CTM is null');
        return coord;
    }

    for (let i = 0; i < coord.length; i += 2) {
        let point = svg.createSVGPoint();
        point.x = coord[i];
        point.y = coord[i + 1];
        point = point.matrixTransform(ctm);
        result.push(point.x, point.y);
    }

    return result;
}
